import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SupportTypes, UserInfo } from '@ca/core/models';
import { DialogService, UserNotificationService, UserService } from '@ca/core/services';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { FullNamePipe } from '../../pipes/full-name/full-name.pipe';
import { CustomerSupportService } from './services/customer-support.service';
import { SupportEmailRequest } from './support-email-request.model';

@UntilDestroy()
@Component({
    selector: 'ca-contact-us',
    templateUrl: './contact-us.component.html',
    styleUrls: ['./contact-us.component.scss'],
})
export class ContactUsComponent implements OnInit {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    @Input() title: string;
    @Input() description: string;
    @Input() messageLabel: string;
    @Input() messagePlaceholder: string;
    @Input() buttonText: string;
    @Input() successModalTitle: string;
    @Input() successModalMessage: string;
    @Input() successModalButtonText: string;
    @Input() close: (reason?: string) => void;
    @Input() selectedSupportType = SupportTypes.GeneralSupport;
    @Input() showSendCopy = false;

    supportTypes = SupportTypes;

    contactUsForm: UntypedFormGroup;

    get userInfo(): UserInfo | undefined {
        return this.userService.myInfo;
    }

    get fullNameControl(): AbstractControl | null {
        return this.contactUsForm.get('fullName');
    }

    get emailControl(): AbstractControl | null {
        return this.contactUsForm.get('email');
    }

    get messageControl(): AbstractControl | null {
        return this.contactUsForm.get('message');
    }

    constructor(
        private activeModal: NgbActiveModal,
        private customerSupportService: CustomerSupportService,
        private fullNamePipe: FullNamePipe,
        private dialogService: DialogService,
        private userNotificationService: UserNotificationService,
        private userService: UserService,
        private translateService: TranslateService,
    ) {}

    ngOnInit(): void {
        this.contactUsForm = new UntypedFormGroup({
            fullName: new UntypedFormControl({ value: this.fullNamePipe.transform(this.userInfo) || '', disabled: !!this.userInfo }, Validators.required),
            email: new UntypedFormControl({ value: this.userInfo?.email || '', disabled: !!this.userInfo }, Validators.required),
            message: new UntypedFormControl('', Validators.required),
            send_copy_to_sender: new UntypedFormControl(true),
        });
    }

    dismiss(reason: string): void {
        this.activeModal.dismiss(reason);
    }

    submit(): void {
        this.userNotificationService.showSpinner();

        const supportEmailRequest: SupportEmailRequest = {
            from_email_address: this.emailControl?.value,
            from_fullname: this.fullNameControl?.value,
            message: this.messageControl?.value,
            support_type: this.selectedSupportType,
        };

        this.customerSupportService
            .sendEmail$(supportEmailRequest)
            .pipe(
                tap(() => {
                    this.userNotificationService.hideSpinner();

                    this.dialogService.showSuccess({
                        title: this.successModalTitle || this.translateService.instant('CONTACT_US.MESSAGE_SENT_SUCCESS_MODAL.HEADER_TITLE'),
                        message: this.successModalMessage || this.translateService.instant('CONTACT_US.MESSAGE_SENT_SUCCESS_MODAL.MESSAGE'),
                        buttonText: this.successModalButtonText || this.translateService.instant('GENERAL.DONE'),
                    });
                    this.activeModal.close();
                }),
                catchError(() => {
                    this.userNotificationService.showError('CONTACT_US.NOTIFICATION.FAILURE');
                    this.userNotificationService.hideSpinner();
                    return EMPTY;
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }
}
